// src/context/ModulesContext.tsx
// Provides per-org module visibility, admin state, and the frontend
// "active module" concept to the whole app.
//
// Backend-driven state (`GET /org/modules`) and frontend-only state
// (active module, localStorage of the last-used module) live here so
// components only read one hook instead of juggling context + router params.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getModules, updateModules, type ModuleDef, type ModulesResponse } from '@/lib/api';
import { isSuperuserFromToken, orgIdFromToken } from '@/lib/api_superadmin';
import { ARCHETYPE_SHELL_DEFAULTS, useDashboardShell } from '@/context/DashboardShellContext';
import {
  defaultTabOf,
  findModule,
  moduleRegistry,
  type ModuleDefinition,
} from '@/modules';

const LAST_MODULE_KEY = 'orion_last_module';
const LAST_MODULE_AT_KEY = 'orion_last_module_at';
// Si el usuario navegó hace poco, recuerda dónde quedó; si pasó más de esto
// (sesión nueva / dejó pasar tiempo), aterriza en el módulo principal del arquetipo.
const LAST_MODULE_FRESH_MS = 1000 * 60 * 60 * 3; // 3 h

// ── Homologación 2026-07-01 ───────────────────────────────────────────────────
// ELIMINADA la whitelist hardcodeada por arquetipo (antes ARCHETYPE_VISIBLE_MODULES).
// Era una SEGUNDA fuente de verdad que se contradecía con el backend → leaks
// (médicos/agencias veían módulos ajenos, y solo 'service-pro' estaba cubierto).
// Ahora el backend es la ÚNICA autoridad: `GET /org/modules` resuelve por
// arquetipo (tabla DB archetype_module_defaults) + override per-org
// (enabled_modules). El frontend OBEDECE: un módulo con `backendModuleKey` se
// muestra SOLO si el backend lo reporta enabled (fail-CLOSED, ver isEnabled).

interface ModulesCtx {
  // ── Backend-driven (as before) ───────────────────────────────
  /** Full list of module definitions with enabled state */
  modules: ModuleDef[];
  /** Quick check: is module key enabled? (unknown keys → true) */
  isEnabled: (key: string) => boolean;
  /** Is the current user an admin? */
  isAdmin: boolean;
  /** Is the current user a developer? (has access to dev-only modules) */
  isDev: boolean;
  /** User's primary role */
  userRole: string | null;
  /** User's complete list of roles */
  userRoles: string[] | null;
  /** Archetype de la org actual. Frontend lo usa para overrides
   *  de label/categoría en el sidebar y default route per-archetype. */
  archetype: string | null;
  /** Org soberana: `/org/modules.sovereign` o, si el backend no lo manda,
   *  arquetipo `government` (sujetos obligados). Oculta servicios comerciales. */
  sovereign: boolean;
  /** Toggle a single module (admin only). Returns updated list. */
  toggle: (key: string, enabled: boolean) => Promise<void>;
  /** Loading state */
  loading: boolean;
  /** Force refetch */
  refresh: () => void;

  // ── Frontend registry helpers (ticket 069) ───────────────────
  /** Every module the current user can see, filtered by backendModuleKey
   *  + dev/CTO requirements. Drives the ModuleSwitcher. */
  availableModules: ModuleDefinition[];
  /** Last-used module key read from localStorage. `null` until rehydrated. */
  lastModuleKey: string | null;
  /** Persist the last-used module (called by <Layout/> on route change). */
  rememberModule: (key: string) => void;
  /** Pick a sensible default module when the URL has none:
   *  last-used → first available → 'config'. */
  defaultModuleKey: () => string;
  /** Home del arquetipo actual (key del registry) — el ModuleSwitcher lo
   *  pinta hasta arriba como "Principal". null si no hay arquetipo. */
  archetypeHomeKey: string | null;
}

const ModulesContext = createContext<ModulesCtx>({
  modules: [],
  isEnabled: () => true,
  isAdmin: false,
  isDev: false,
  userRole: null,
  userRoles: null,
  archetype: null,
  sovereign: false,
  toggle: async () => {},
  loading: true,
  refresh: () => {},
  availableModules: [],
  lastModuleKey: null,
  rememberModule: () => {},
  defaultModuleKey: () => 'config',
  archetypeHomeKey: null,
});

export function ModulesProvider({ children }: { children: React.ReactNode }) {
  const [modules, setModules] = useState<ModuleDef[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDev, setIsDev] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<string[] | null>(null);
  const [archetype, setArchetype] = useState<string | null>(null);
  const [sovereignFlag, setSovereignFlag] = useState<boolean | null>(null);
  // Menú visible por org (config `menu_dashboard`, lo manda el backend). null =
  // sin restricción, que es lo de todas las orgs salvo las que lo configuren.
  const [menuVisible, setMenuVisible] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastModuleKey, setLastModuleKey] = useState<string | null>(() => {
    // Solo recordar el último módulo si fue reciente (frescura). Si pasó tiempo
    // → null → defaultModuleKey aterriza en el home del arquetipo.
    try {
      const k = localStorage.getItem(LAST_MODULE_KEY);
      const at = Number(localStorage.getItem(LAST_MODULE_AT_KEY) || 0);
      if (k && Date.now() - at < LAST_MODULE_FRESH_MS) return k;
    } catch {
      /* private-mode */
    }
    return null;
  });

  const fetchModules = useCallback(async () => {
    // Reintentos: justo tras el login el token puede no estar listo en el primer
    // request (ensureFreshToken/timing) → un fallo transitorio hacía que se
    // mostraran TODOS los módulos (incl. de otro vertical, ej. "Agencia de
    // Viajes"). Reintentamos antes de rendirnos para que se auto-cure.
    let lastErr: unknown = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const data: ModulesResponse = await getModules();
        setModules(data.modules);
        setIsAdmin(data.is_admin);
        setIsDev(data.is_dev ?? false);
        setUserRole(data.user_role ?? null);
        setUserRoles(data.user_roles ?? null);
        setArchetype(data.archetype ?? null);
        setSovereignFlag(typeof data.sovereign === 'boolean' ? data.sovereign : null);
        setMenuVisible(data.menu_visible?.length ? data.menu_visible : null);
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    console.warn('⚠️ No se pudieron cargar los módulos tras reintentos:', lastErr);
    // Fallback FAIL-CLOSED (homologación): NO habilitar todo. Dejamos modules
    // vacío → isEnabled (tras loading) niega los módulos con backendModuleKey;
    // solo quedan los core sin key (empresa/config). NUNCA se cuelan módulos de
    // otro vertical por un fallo de red. `auth:token-ready` fuerza un refetch.
    setModules([]);
    setIsAdmin(false);
    setIsDev(false);
    setUserRole(null);
    setUserRoles(null);
    setArchetype(null);
    setSovereignFlag(null);
    setMenuVisible(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    // Only fetch if authenticated
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchModules();
    } else {
      setLoading(false);
    }
    // Refetch cuando el login acaba de setear el token (mismo patrón que otros
    // componentes con race de auth) → evita el estado degradado en el 1er render.
    const onTokenReady = () => {
      setLoading(true);
      fetchModules();
    };
    window.addEventListener('auth:token-ready', onTokenReady);
    return () => window.removeEventListener('auth:token-ready', onTokenReady);
  }, [fetchModules]);

  const isEnabled = useCallback(
    (key: string): boolean => {
      // Durante la carga inicial permitimos (evita flash); el RootRedirect igual
      // muestra "Cargando módulos…" hasta que loading=false. Una vez cargado
      // (aunque el fetch fallara → modules=[]), fail-CLOSED: los módulos con
      // backendModuleKey se NIEGAN si el backend no los reporta habilitados —
      // así NO se cuela "Agencia de Viajes" ni otro vertical por un fallo.
      if (loading) return true;
      const mod = modules.find((m) => m.key === key);
      // fail-CLOSED (homologación): key desconocida = NO habilitada. Junto con
      // keys canónicas (backendModuleKey == key del backend), el backend es la
      // única autoridad y no se cuelan módulos ajenos al arquetipo/org.
      return mod ? mod.enabled : false;
    },
    [modules, loading],
  );

  const toggle = useCallback(
    async (key: string, enabled: boolean) => {
      const data = await updateModules({ [key]: enabled });
      setModules(data.modules);
      setIsAdmin(data.is_admin);
      setIsDev(data.is_dev ?? false);
      setUserRole(data.user_role ?? null);
      setUserRoles(data.user_roles ?? null);
      setArchetype(data.archetype ?? null);
    },
    [],
  );

  // ── Ticket-069 helpers ────────────────────────────────────
  const hasDevAccess = useMemo(
    () =>
      !!(userRoles && (userRoles.includes('cto') || userRoles.includes('developer'))),
    [userRoles],
  );
  const isCtoRole = useMemo(
    () => !!(userRoles && userRoles.includes('cto')),
    [userRoles],
  );

  // requireSuperuser: gating client-side por is_superuser=true en el JWT.
  // Server-side enforcement vive en thot_admin_auth (apps/superadmin/auth.py).
  const isSuperuser = useMemo(() => isSuperuserFromToken(), []);

  // org_id del JWT — para ocultar módulos per-org (hiddenForOrgs). Fail-open:
  // si no hay org_id, no oculta nada.
  const currentOrgId = useMemo(() => orgIdFromToken(), []);

  // AUDIENCIA del usuario (portales staff/cliente/socio). La deriva el rol:
  // cliente_final/customer → 'client'; vendedor/socio → 'partner'; resto 'staff'.
  // El backend YA restringe /org/modules por rol (defensa en profundidad); esto
  // además oculta los módulos core sin backendModuleKey (empresa/config) a
  // clientes/socios, para que vean SOLO su portal.
  const userAudience = useMemo<'staff' | 'client' | 'partner'>(() => {
    const r = new Set((userRoles ?? []).map((x) => String(x).toLowerCase()));
    if (r.has('cliente_final') || r.has('customer')) return 'client';
    if (r.has('vendedor') || r.has('socio') || r.has('partner')) return 'partner';
    return 'staff';
  }, [userRoles]);

  // Shell del dashboard de la org (opt-in por datos). 'classic' para toda org
  // que no lo configure → los módulos con `requireShell` no existen para ella.
  const { shell: resolvedShell, config: shellConfig, setArchetype: setShellArchetype } = useDashboardShell();
  // Mismo render en que llega el arquetipo: si su giro trae el shell Orion por
  // defecto (y la org no lo apagó por datos), los módulos del shell ya cuentan —
  // sin esperar al efecto del proveedor (si no, un enlace directo a /m/orion/…
  // rebotaba al home en la primera carga).
  const shell = resolvedShell === 'orion'
    || (archetype && ARCHETYPE_SHELL_DEFAULTS[archetype]?.shell === 'orion' && shellConfig?.shell !== 'classic')
    ? 'orion' : resolvedShell;
  // El shell tiene default por arquetipo (p. ej. government → SO Orion): se lo avisamos.
  useEffect(() => { setShellArchetype(archetype); }, [archetype, setShellArchetype]);

  // Lo manda el backend si lo sabe; si no, gobierno = soberano por defecto.
  const sovereign = sovereignFlag ?? archetype === 'government';

  const availableModules = useMemo<ModuleDefinition[]>(() => {
    // Homologación: el BACKEND es la autoridad de qué módulos tiene la org
    // (por arquetipo + override per-org). El rol solo RESTRINGE dentro de lo
    // habilitado; ya NO hay bypass de CTO que salte el gate (por eso antes se
    // veían módulos de otro vertical al entrar como cto/god-mode).
    return moduleRegistry.filter((m) => {
      // 0) Audiencia: el usuario ve SOLO módulos de SU portal (default 'staff').
      const aud = m.audiences ?? ['staff'];
      if (!aud.includes(userAudience)) return false;
      // 0.5) Per-org: módulos ocultos para esta org (fail-OPEN — sin la lista o
      //      org no incluida → visible; no afecta a ninguna otra org).
      if (currentOrgId && m.hiddenForOrgs?.includes(currentOrgId)) return false;
      // 0.55) Módulos propios de un shell (p.ej. Inicio de «Orion»): solo si la
      //       org activó ESE shell por datos. Ninguna otra org los ve.
      if (m.requireShell && m.requireShell !== shell) return false;
      // 0.6) Menú visible configurado por la org (Mi Mercadito, 08-oct: «deja
      //      solo bazares y conversaciones»). Solo navegación: los módulos y las
      //      herramientas del agente siguen igual. Sin configurar → no filtra.
      if (menuVisible && !menuVisible.includes(m.key)) return false;
      // 1) Autoridad backend: si declara backendModuleKey, debe estar habilitado.
      //    (módulos sin key = core como empresa/config → siempre visibles).
      if (m.backendModuleKey && !isEnabled(m.backendModuleKey)) return false;
      // 1.1) Gate tolerante: se obedece solo si el backend ya conoce la key.
      if (m.softBackendModuleKey && !loading) {
        const known = modules.find((x) => x.key === m.softBackendModuleKey);
        if (known && !known.enabled) return false;
      }
      // 1.2) Orgs soberanas: nada de servicios comerciales extranjeros.
      if (m.hideForSovereign && sovereign) return false;
      // 2) Restricciones por rol, DENTRO de lo ya habilitado.
      // 🚨 2026-07-24 · Si no puedes entrar, no lo ves.
      // Los módulos de administración (empresa/config) no tienen
      // backendModuleKey, así que pasaban SIEMPRE — y un empleado que los abría
      // recibía "Insufficient permissions". Ahora se ocultan del selector.
      // requireAdmin rige el portal de STAFF. Un módulo que además declara la
      // audiencia 'partner' (Conversaciones) ya optó por servir a vendedoras, y
      // el servidor les limita los datos a lo suyo. Hoy ningún otro módulo con
      // requireAdmin declara partner/client, así que esto no cambia a ningún otro.
      if (m.requireAdmin && userAudience === 'staff' && !isAdmin && !hasDevAccess) return false;
      if (m.requireSuperuser && !isSuperuser) return false;
      if (m.devOnly && !hasDevAccess) return false;
      if (m.requireCto && !isCtoRole) return false;
      // Gate por ROL arbitrario (p.ej. 'comercio') — admin/dev SIEMPRE pasa
      // (bypass), el resto debe traer al menos uno de los roles listados.
      // El candado real de datos lo pone el backend (scope); esto solo evita
      // ofrecer en el sidebar algo que devolvería 403.
      if (m.requireRoles && m.requireRolesStrict && !(userRoles ?? []).some((r) => m.requireRoles!.includes(r))) return false;
      if (
        m.requireRoles &&
        m.requireRoles.length > 0 &&
        !isAdmin &&
        !hasDevAccess &&
        !(userRoles ?? []).some((r) => m.requireRoles!.includes(r))
      )
        return false;
      return true;
    });
  }, [archetype, hasDevAccess, isCtoRole, isEnabled, isSuperuser, currentOrgId, userAudience, isAdmin, userRoles, menuVisible, shell, sovereign, modules, loading]);

  const rememberModule = useCallback((key: string) => {
    if (!findModule(key)) return;
    setLastModuleKey(key);
    try {
      localStorage.setItem(LAST_MODULE_KEY, key);
      localStorage.setItem(LAST_MODULE_AT_KEY, String(Date.now()));
    } catch {
      /* quota / private-mode — non-fatal */
    }
  }, []);

  // Default module por archetype — para que cada tipo de empresa
  // aterrice en la vista más relevante. healthcare-clinic entra al
  // Consultorio (cockpit home) en vez de un módulo random.
  // Módulo "home/principal" de CADA arquetipo — al que se aterriza en sesión
  // nueva. TODOS los arquetipos tienen uno (si el específico no está disponible
  // para la org, se cae al primero disponible). El value es la key del registry.
  const ARCHETYPE_DEFAULT_MODULE: Record<string, string> = {
    'healthcare-clinic': 'clinical-cockpit',   // Consultorio
    'agency-travel': 'travel-sales',           // Ventas — Agencia de Viajes
    'agency-marketing': 'mkt-meta',            // Marketing
    'consumer-brand': 'mkt-meta',
    'personal-brand': 'mkt-meta',
    'educational-institution': 'university-bi', // Panel de inteligencia (universidades B2B)
    'government': 'citizen-reports',            // Atención Ciudadana (Centro de Inteligencia Urbana)
    // Una rentadora abre el día en la flota, no en el CRM: la pregunta #1 es
    // "¿tengo esta máquina libre?". Esto además decide dónde aterriza el
    // usuario al entrar en sesión nueva.
    'service-pro': 'rental-assets',
    'platform': 'crm',
    'demo': 'crm',
  };

  const defaultModuleKey = useCallback((): string => {
    // 1. Último módulo — SOLO si fue reciente (lastModuleKey ya expira por
    //    frescura) y sigue disponible. Así se recuerda dónde quedó el usuario.
    if (lastModuleKey && availableModules.some((m) => m.key === lastModuleKey)) {
      return lastModuleKey;
    }
    // 2. Home del arquetipo (el "principal" en sesión nueva / tras pasar tiempo).
    const byArchetype = archetype ? ARCHETYPE_DEFAULT_MODULE[archetype] : null;
    if (byArchetype && availableModules.some((m) => m.key === byArchetype)) {
      return byArchetype;
    }
    // 3. First available module that has at least one non-coming-soon tab.
    const shipped = availableModules.find(
      (m) => !m.comingSoon && m.sidebarItems.some((s) => !s.comingSoon),
    );
    if (shipped) return shipped.key;
    // 4. First available module overall.
    if (availableModules.length > 0) return availableModules[0].key;
    // 5. Fallback to config — always visible.
    return 'config';
  }, [availableModules, lastModuleKey, archetype]);

  return (
    <ModulesContext.Provider
      value={{
        modules,
        isEnabled,
        isAdmin,
        isDev,
        userRole,
        userRoles,
        archetype,
        sovereign,
        toggle,
        loading,
        refresh: fetchModules,
        availableModules,
        lastModuleKey,
        rememberModule,
        defaultModuleKey,
        archetypeHomeKey: (archetype && ARCHETYPE_DEFAULT_MODULE[archetype]) || null,
      }}
    >
      {children}
    </ModulesContext.Provider>
  );
}

export const useModules = () => useContext(ModulesContext);

export { LAST_MODULE_KEY, defaultTabOf };
